import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, AlertTriangle } from 'lucide-react';
import { TIMEZONE_OPTIONS, COUNTRY_SUGGESTED_TIMEZONE, timezoneLabel } from '../../constants/timezones';
import { api, apiClient } from '../../api/client';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { unwrap } from '../../lib/api-envelope';
import { formatCalendarDate, todayCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import ProvinceSelect, { regionName } from '../../components/ProvinceSelect';
import ConfirmModal from '../../components/ui/ConfirmModal';
import TaxRateRowModal, { type TaxRateModalMode } from '../../components/settings/TaxRateRowModal';
import {
  groupByJurisdiction,
  rowEffect,
  rowInEffectAfterRemoving,
  fmtTaxRate,
  fmtRateLabel,
  formValuesFromRow,
  changeFormForJurisdiction,
  addFormForJurisdiction,
  startOfYear,
  taxStatusLine,
  EMPTY_TAX_RATE_FORM,
  ROW_EFFECT_LABELS,
  type TaxRateRow,
  type TaxRatesPayload,
  type StandardTaxRate,
  type TaxRateFormValues,
  type RowEffect,
} from '../../lib/company-tax-rates';
import { taxSystemLabel } from '../../constants/tax-systems';
import { toast } from 'sonner';
import { SettingsCard, SettingsPage } from '../../components/settings/SettingsPage';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';
const selectCls = inputCls;

type Country = 'CA' | 'US';

/**
 * What changing the country changes. The two tax LABEL defaults that used to
 * live here went with the company-column rate fields — labels are stated per
 * jurisdiction now, on the row that carries the rate they name.
 */
const COUNTRY_DEFAULTS: Record<Country, {
  currency: 'CAD' | 'USD';
  unit_system: 'metric' | 'imperial';
  well_id_format: 'LSD' | 'API';
  date_format: 'YYYY-MM-DD' | 'MM/DD/YYYY';
  province_state: string;
}> = {
  CA: {
    currency: 'CAD',
    unit_system: 'metric',
    well_id_format: 'LSD',
    date_format: 'YYYY-MM-DD',
    province_state: 'AB',
  },
  US: {
    currency: 'USD',
    unit_system: 'imperial',
    well_id_format: 'API',
    date_format: 'MM/DD/YYYY',
    province_state: 'TX',
  },
};

/**
 * The company-level settings this page owns.
 *
 * `tax_rate_1`, `tax_rate_2` and their two labels USED TO BE HERE and are
 * gone on purpose — see the header comment on the jurisdiction section below.
 * `tax_number` stays: it is a registration number printed on documents, not a
 * rate, and nothing resolves through it.
 *
 * `well_id_format` is `''` when the page has not been told the saved value,
 * which is always — `GET /settings/regional-tax` does not return it (nor
 * `province_state`; that one is recoverable from `home_jurisdiction`). `''`
 * means "leave it alone" and is not sent. It stays on the page because it is
 * live: JobDetail labels the well ID from it, through /company/locale.
 *
 * NOT HERE, ON PURPOSE: `language` and `temperature_unit`. The PATCH accepts
 * both and nothing reads either — no template, no locale helper (temperature
 * display follows `unit_system`). They are hidden rather than deleted, and
 * never sent, so a stored value is left exactly as it is.
 */
interface RegionalForm {
  country: Country;
  province_state: string;
  currency: 'CAD' | 'USD';
  unit_system: 'metric' | 'imperial';
  well_id_format: '' | 'LSD' | 'API';
  date_format: 'YYYY-MM-DD' | 'MM/DD/YYYY' | 'DD/MM/YYYY';
  time_zone: string;
  tax_number: string;
}

// Accept both legacy "Canada" / "USA" strings and ISO codes from the API.
function normalizeCountry(raw: any): Country {
  if (raw === 'CA' || raw === 'Canada' || raw === 'CAN') return 'CA';
  if (raw === 'US' || raw === 'USA' || raw === 'United States') return 'US';
  return 'CA';
}

/*
 * The hue is on the EDGE and the wash; the word is in theme ink. A signal hue
 * as text (a bright green or blue on a 10% wash of itself) reads under 2:1 on the white
 * light-mode card — the label has to carry the state, so it has to be legible.
 */
const EFFECT_BADGE: Record<RowEffect, string> = {
  in_effect: 'bg-emerald-500/10 text-fieldiq-text border-emerald-500/50',
  scheduled: 'bg-sky-500/10 text-fieldiq-text border-sky-500/50',
  superseded: 'bg-fieldiq-dark text-fieldiq-text border-fieldiq-border',
};

/** A calendar day, never through `new Date()`. Absent renders as an em dash. */
const day = (value: string | null | undefined) => formatCalendarDate(value) ?? '—';

export default function RegionalTaxPage() {
  const queryClient = useQueryClient();
  const [initialized, setInitialized] = useState(false);

  /*
   * Who may write. PUT/DELETE /settings/tax-rates and PATCH
   * /settings/regional-tax are all non-strict `requireRole('admin')`: admin, or
   * a dispatcher on a tenant with dispatcher_admin_access. Not `owner`. Both
   * GETs are ungated, so the table itself is readable by everyone.
   */
  const canWrite = useCanWriteCompanySettings();
  // Which row is "in effect" is a question about the TENANT's today. Null
  // only when the zone is unknown — this page stays reachable then, so an
  // admin can set one — and then no row claims a status.
  const tenantToday = todayCalendarDate(useTenantTimeZone());

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'regional-tax'],
    queryFn: () => apiClient.get('/settings/regional-tax').then(r => unwrap<Record<string, any>>(r.data) ?? {}),
  });

  /*
   * THE DEDICATED ENDPOINT, not the `jurisdictions[]` that GET
   * /settings/regional-tax also carries. Three reasons, in order of weight:
   *
   *   1. The writes are on THIS resource. `PUT /settings/tax-rates` and
   *      `DELETE /settings/tax-rates/:id` invalidate one key that means
   *      exactly the list they changed.
   *   2. The regional-tax payload is copied into form state once, behind
   *      `!initialized`. A list read through that path is frozen at first load
   *      and stale the moment a row is saved — so the fix would be either a
   *      fifteenth key that has to be re-synced by hand after every mutation,
   *      or a second hydration path in the same effect reading around the
   *      form. Both are the bug the effect already has, one field wider.
   *   3. `{ home_jurisdiction, rows }` is the narrower contract, with nothing
   *      else in it to drift.
   *
   * regional-tax is still read, for the company settings above and for
   * `tax_source` — which is the only thing that says whether a document is
   * being priced by a row here or by the legacy company columns.
   */
  const { data: taxRates, isLoading: ratesLoading, isError: ratesError, refetch: refetchRates } = useQuery({
    queryKey: ['settings', 'tax-rates'],
    queryFn: () => api.taxRates.list().then(r => unwrap<TaxRatesPayload>(r.data)),
  });

  // The statutory table "Add jurisdiction" fills from. A constant on the API,
  // so it never goes stale within a session. A failure is said in the modal
  // (enter the rates yourself) rather than silently leaving the fields blank.
  const { data: standardRates, isError: standardRatesFailed } = useQuery({
    queryKey: ['settings', 'standard-tax-rates'],
    queryFn: () => api.taxRates.standard().then(r => unwrap<{ rows: StandardTaxRate[] }>(r.data)?.rows ?? []),
    staleTime: Infinity,
    enabled: canWrite,
  });

  const [form, setForm] = useState<RegionalForm>({
    country: 'CA',
    province_state: '',
    currency: 'CAD',
    unit_system: 'metric',
    well_id_format: '',
    date_format: 'YYYY-MM-DD',
    // The stored zone, or '' — never a default. A select seeded with
    // Edmonton showed Edmonton for any company whose read had not landed, and
    // the country switch below used to overwrite a set zone outright.
    time_zone: '',
    tax_number: '',
  });
  /*
   * WHICH FIELDS THE USER ACTUALLY TOUCHED, and the only ones the PATCH sends.
   *
   * The old page sent the whole form on every Save. Three of its fields are
   * not in the GET payload at all — `province_state`, `well_id_format`, and
   * the since-hidden `temperature_unit` — so they hydrated from hardcoded defaults and were
   * written back as AB / LSD / celsius by anyone who opened the page and saved
   * a timezone. On a US tenant that silently moved the HOME JURISDICTION to
   * Alberta, which since API #530 decides which tax row prices a
   * province-less document.
   *
   * A PATCH is partial by definition; sending an unread field as a guess is
   * the write half of the same mistake as rendering one.
   */
  const [touched, setTouched] = useState<Set<keyof RegionalForm>>(new Set());
  const dirty = touched.size > 0;

  useEffect(() => {
    if (data && !initialized) {
      const raw = data as Record<string, any>;
      setForm(prev => ({
        ...prev,
        country: normalizeCountry(raw.country ?? prev.country),
        // `home_jurisdiction` IS `companies.province_state`, uppercased — the
        // payload has no `province_state` key of its own, and the old
        // `raw.province_state ?? raw.province ?? raw.state` chain therefore
        // never matched anything and always landed on the default.
        province_state: raw.home_jurisdiction ?? '',
        currency: raw.currency ?? prev.currency,
        unit_system: raw.unit_system ?? prev.unit_system,
        date_format: raw.date_format ?? prev.date_format,
        time_zone: typeof raw.time_zone === 'string' ? raw.time_zone : '',
        tax_number: raw.tax_number ?? '',
      }));
      setInitialized(true);
      setTouched(new Set());
    }
  }, [data, initialized]);

  const update = <K extends keyof RegionalForm>(key: K, value: RegionalForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setTouched(prev => new Set(prev).add(key));
  };

  const suggestedZone = COUNTRY_SUGGESTED_TIMEZONE[form.country];

  const onCountryChange = (next: Country) => {
    const d = COUNTRY_DEFAULTS[next];
    setForm(prev => ({
      ...prev,
      country: next,
      province_state: d.province_state,
      currency: d.currency,
      unit_system: d.unit_system,
      well_id_format: d.well_id_format,
      date_format: d.date_format,
      // NOT the time zone. Changing the country SUGGESTS one (below the
      // field) and never rewrites a zone that is already set: every date in
      // the app is computed in it, and a country edit is not a decision about
      // the clock (fe#tenant-zone).
    }));
    setTouched(prev => {
      const next2 = new Set(prev);
      (['country', 'province_state', 'currency', 'unit_system', 'well_id_format',
        'date_format'] as const).forEach(k => next2.add(k));
      return next2;
    });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {};
      for (const key of Array.from(touched)) {
        const value = form[key];
        // '' on the unread select means "leave it alone", not "clear it".
        if (key === 'well_id_format' && value === '') continue;
        payload[key] = value;
      }
      return apiClient.patch('/settings/regional-tax', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'regional-tax'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'tax-rates'] });
      queryClient.invalidateQueries({ queryKey: ['company', 'locale'] });
      // The Notifications workspace shows this zone read-only beside the daily
      // alert time, from its own endpoint; without this it reads the old zone
      // for up to staleTime after a change here.
      queryClient.invalidateQueries({ queryKey: ['company', 'alert-settings'] });
      setTouched(new Set());
      toast.success('Regional settings saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  // ── The jurisdiction table ────────────────────────────────────────────────

  const [modalMode, setModalMode] = useState<TaxRateModalMode | null>(null);
  const [modalValues, setModalValues] = useState<TaxRateFormValues>(EMPTY_TAX_RATE_FORM);
  const [modalRow, setModalRow] = useState<TaxRateRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<TaxRateRow | null>(null);

  const rows = useMemo(() => taxRates?.rows ?? [], [taxRates]);
  const homeJurisdiction = taxRates?.home_jurisdiction ?? null;
  const groups = useMemo(
    () => groupByJurisdiction(rows, homeJurisdiction),
    [rows, homeJurisdiction],
  );

  const saveRow = useMutation({
    mutationFn: api.taxRates.save,
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'tax-rates'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'regional-tax'] });
      setModalMode(null);
      toast.success(`${vars.jurisdiction} saved, effective ${vars.effective_from}`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save the rate'),
  });

  const removeRow = useMutation({
    mutationFn: (id: string) => api.taxRates.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'tax-rates'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'regional-tax'] });
      setDeleteRow(null);
      toast.success('Jurisdiction rate deleted');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to delete the rate'),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  const provinceLabel = form.country === 'CA' ? 'Province' : 'State';
  const taxSource = (data as Record<string, any>)?.tax_source as string | undefined;
  const taxUnconfiguredReason = (data as Record<string, any>)?.tax_unconfigured_reason as string | null | undefined;

  // Add opens on 1 January of the tenant's year, not today — see
  // addFormForJurisdiction. Opened from the status line it arrives with the
  // home province already picked and filled.
  const openAdd = (jurisdiction?: string) => {
    setModalRow(null);
    setModalValues(jurisdiction
      ? addFormForJurisdiction(jurisdiction, standardRates, startOfYear(tenantToday))
      : { ...EMPTY_TAX_RATE_FORM, effective_from: startOfYear(tenantToday) });
    setModalMode('add');
  };
  const openChange = (jurisdiction: string) => {
    setModalRow(null);
    setModalValues(changeFormForJurisdiction(jurisdiction));
    setModalMode('change');
  };
  const openCorrect = (row: TaxRateRow) => {
    setModalRow(row);
    setModalValues(formValuesFromRow(row));
    setModalMode('correct');
  };

  const status = taxStatusLine({
    taxSource,
    unconfiguredReason: taxUnconfiguredReason,
    homeJurisdiction,
    rows,
    today: tenantToday,
  });

  /** What takes over if `deleteRow` goes — null when nothing else applies. */
  const successor = deleteRow && tenantToday ? rowInEffectAfterRemoving(deleteRow, rows, tenantToday) : null;

  return (
    <div className="min-h-full">
      {/* The Finance & region workspace renders the title and the tab strip. */}
      <SettingsPage>
        {/* Regional */}
        <SettingsCard title="Regional">
          {/* Four fields to a line from 1440 wide, two below it. */}
          <div className="grid gap-3 min-[1440px]:grid-cols-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="country">Country</label>
              <select id="country" className={selectCls} value={form.country} onChange={e => onCountryChange(e.target.value as Country)} disabled={!canWrite}>
                <option value="CA">Canada</option>
                <option value="US">United States</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="province_state">{provinceLabel}</label>
              {/* The HOME jurisdiction. A document with no province of its own
                  is priced by the row for this code, so it is not just an
                  address field — changing it changes which rate answers. */}
              <ProvinceSelect
                id="province_state"
                value={form.province_state}
                onChange={v => update('province_state', v)}
                country={form.country}
                className={selectCls}
                disabled={!canWrite}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="currency">Currency</label>
              <select id="currency" className={selectCls} value={form.currency} onChange={e => update('currency', e.target.value as 'CAD' | 'USD')} disabled={!canWrite}>
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="unit_system">Unit System</label>
              <select id="unit_system" className={selectCls} value={form.unit_system} onChange={e => update('unit_system', e.target.value as 'metric' | 'imperial')} disabled={!canWrite}>
                <option value="metric">Metric (m, kPa, °C)</option>
                <option value="imperial">Imperial (ft, PSI, °F)</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="well_id_format">Well ID Format</label>
              {/* GET /settings/regional-tax does not return this column, so the
                  page cannot show what is saved. The blank option is the
                  default and is NOT sent — the old page defaulted the control
                  to LSD and wrote that back on any save. */}
              <select id="well_id_format" className={selectCls} value={form.well_id_format} onChange={e => update('well_id_format', e.target.value as RegionalForm['well_id_format'])} disabled={!canWrite}>
                <option value="">Leave unchanged</option>
                <option value="LSD">LSD / UWI (Canada)</option>
                <option value="API">API Number (US)</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="date_format">Date Format</label>
              <select id="date_format" className={selectCls} value={form.date_format} onChange={e => update('date_format', e.target.value as RegionalForm['date_format'])} disabled={!canWrite}>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="time_zone">Time Zone</label>
              {/* Options come from the shared mirror of the API's allowlist, not
                  from a list kept here. The inline list this replaced was missing
                  St_Johns, Regina and Phoenix, so Newfoundland and both no-DST
                  zones could not be selected at all. */}
              <select id="time_zone" className={selectCls} value={form.time_zone} onChange={e => update('time_zone', e.target.value)} disabled={!canWrite}>
                {/* A select cannot show a value it has no option for — it shows
                    the first one while state keeps the real value. So an unset
                    zone gets a "Not set" option, and a stored zone outside the
                    list gets its own, rather than reading as St. John's. */}
                {form.time_zone === '' && <option value="" disabled>Not set</option>}
                {form.time_zone !== '' && !TIMEZONE_OPTIONS.some(tz => tz.value === form.time_zone) && (
                  <option value={form.time_zone}>{timezoneLabel(form.time_zone)}</option>
                )}
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
              {canWrite && touched.has('country') && form.time_zone !== suggestedZone && (
                <p className="text-xs text-fieldiq-text mt-1" data-testid="time-zone-suggestion">
                  Suggested zone for {form.country === 'CA' ? 'Canada' : 'the United States'}: {timezoneLabel(suggestedZone)}.{' '}
                  <button type="button" className="underline text-white" onClick={() => update('time_zone', suggestedZone)}>
                    Use this zone
                  </button>
                </p>
              )}
              {/* The ONE control for companies.time_zone. Notifications used to
                  carry a second one for the same column; it now shows this
                  value read-only and links here. */}
              <p className="text-xs text-fieldiq-text mt-1">The daily job alert is sent in this zone.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="tax_number">Tax Number</label>
              <input id="tax_number" className={inputCls} value={form.tax_number} onChange={e => update('tax_number', e.target.value)} disabled={!canWrite} placeholder={form.country === 'CA' ? '123456789 RT0001' : 'Federal EIN or state tax ID'} />
            </div>
          </div>
          </div>
          <p className="text-xs text-fieldiq-text">Tax number: printed on invoices and field tickets.</p>

          {canWrite ? (
            <div className="flex justify-end">
              <button
                onClick={() => saveMutation.mutate()}
                disabled={!dirty || saveMutation.isPending}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
                }`}
              >
                {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
                {saveMutation.isPending ? 'Saving...' : 'Save regional settings'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-fieldiq-text">Only an admin can change these settings.</p>
          )}
        </SettingsCard>

        {/* ── Tax by jurisdiction ───────────────────────────────────────────
            WHY THE RATE FIELDS ARE NOT IN THE SECTION ABOVE ANY MORE.

            `companies.tax_system / tax_rate_1 / tax_rate_2` is what this page
            used to edit, and `resolveCompanyTax` reads those columns SECOND —
            after the jurisdiction row for the document's province. Every one
            of the twelve tenants with a home province has such a row (API
            migration 0369 seeded one from those very columns), so the columns
            were shadowed on every tenant in the estate: typing a new GST rate
            up there saved successfully, showed a success toast, and changed
            nothing about what any document was priced at.

            Two editors for one number, one of them inert, is the surprise. The
            rates live here now, on the row that actually answers. */}
        <SettingsCard
          title="Tax by jurisdiction"
          help={<>Tax is charged for the {provinceLabel.toLowerCase()} where the work was done. Documents with no {provinceLabel.toLowerCase()} use {homeJurisdiction ?? `your home ${provinceLabel.toLowerCase()}`}.</>}
          actions={canWrite && (
            <button
              onClick={() => openAdd()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 shrink-0"
            >
              <Plus size={15} /> Add jurisdiction
            </button>
          )}
        >

          {/* ONE line, shown only when the home jurisdiction's tax is actually
              missing something, with the one action that fixes it. It replaced
              two stacked banners that usually appeared together and said the
              same thing twice — see taxStatusLine. Not shown while the rows
              are loading or failed: the refusal below speaks for itself. */}
          {status && !ratesLoading && !ratesError && (
            <div
              role={taxUnconfiguredReason ? 'alert' : 'status'}
              data-testid="tax-status-line"
              className="flex items-center gap-3 flex-wrap rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm text-white"
            >
              <AlertTriangle size={16} className="shrink-0 text-fieldiq-accent-signal" aria-hidden="true" />
              <span className="flex-1 min-w-[12rem]">{status.message}</span>
              {canWrite && status.action && (
                <button
                  type="button"
                  onClick={() => {
                    const a = status.action!;
                    if (a.kind === 'add') openAdd(a.jurisdiction);
                    else openCorrect(a.row);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 shrink-0"
                >
                  {status.action.label}
                </button>
              )}
            </div>
          )}

          {ratesLoading && (
            <div className="flex items-center gap-2 py-6 text-sm text-fieldiq-text"><Loader2 size={16} className="animate-spin" /> Loading jurisdictions…</div>
          )}

          {/* A failed read is a REFUSAL, not an empty state. Offering "Add
              jurisdiction" over a list that could not be fetched turns a read
              failure into a duplicate write. */}
          {!ratesLoading && ratesError && (
            <div role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-white flex items-center justify-between gap-3">
              <span>Could not load the jurisdiction rates.</span>
              <button onClick={() => refetchRates()} className="px-3 py-1.5 rounded-lg border border-red-500/50 text-white text-xs font-semibold hover:bg-red-500/10">Retry</button>
            </div>
          )}

          {!ratesLoading && !ratesError && groups.length === 0 && (
            <div className="rounded-lg border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text">
              No provinces yet.{canWrite ? ' Add one to start charging tax.' : ''}
            </div>
          )}

          {!ratesLoading && !ratesError && groups.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-fieldiq-border">
              <table className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="bg-fieldiq-dark text-left text-xs uppercase tracking-wide text-fieldiq-text">
                    <th className="px-4 py-2 font-medium">Jurisdiction</th>
                    <th className="px-4 py-2 font-medium">Effective from</th>
                    <th className="px-4 py-2 font-medium">Tax system</th>
                    <th className="px-4 py-2 font-medium">Rate 1</th>
                    <th className="px-4 py-2 font-medium">Rate 2</th>
                    {canWrite && <th className="px-4 py-2 font-medium text-right sticky right-0 bg-fieldiq-dark">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {groups.map(group => group.rows.map((row, i) => {
                    const effect = tenantToday ? rowEffect(row, rows, tenantToday) : null;
                    return (
                      <tr key={row.id} className={`border-t border-fieldiq-border bg-fieldiq-card ${i === 0 ? '' : 'border-t-fieldiq-border/40'}`}>
                        <td className="px-4 py-3 align-top">
                          {i === 0 ? (
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-white">{group.jurisdiction}</span>
                                {group.jurisdiction === homeJurisdiction && (
                                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-fieldiq-accent/50 text-fieldiq-accent-signal">Home</span>
                                )}
                              </div>
                              <div className="text-xs text-fieldiq-text">{group.name === group.jurisdiction ? '' : group.name}</div>
                            </div>
                          ) : (
                            <span className="text-fieldiq-text" aria-hidden="true">↳</span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-white">{day(row.effective_from)}</div>
                          {effect ? (
                            <span className={`inline-block mt-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${EFFECT_BADGE[effect]}`}>
                              {ROW_EFFECT_LABELS[effect]}
                            </span>
                          ) : (
                            <span className="block mt-1 text-[10px] text-fieldiq-text">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-fieldiq-text">{taxSystemLabel(row.tax_system)}</td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-white">{fmtTaxRate(row.tax_rate_1)}</div>
                          <div className="text-xs text-fieldiq-text">{fmtRateLabel(row.tax_rate_1_label)}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-white">{fmtTaxRate(row.tax_rate_2)}</div>
                          <div className="text-xs text-fieldiq-text">{fmtRateLabel(row.tax_rate_2_label)}</div>
                        </td>
                        {canWrite && (
                          <td className="px-4 py-3 align-top text-right sticky right-0 bg-fieldiq-card">
                            <div className="flex items-center gap-2 justify-end whitespace-nowrap">
                              {i === 0 && (
                                <button
                                  onClick={() => openChange(group.jurisdiction)}
                                  className="px-2.5 py-1.5 rounded-lg bg-fieldiq-card border border-fieldiq-border text-xs font-semibold text-white hover:border-fieldiq-accent/50"
                                >
                                  Rate change
                                </button>
                              )}
                              <button
                                onClick={() => openCorrect(row)}
                                className="px-2.5 py-1.5 rounded-lg bg-fieldiq-card border border-fieldiq-border text-xs font-semibold text-white hover:border-fieldiq-accent/50"
                              >
                                Correct
                              </button>
                              <button
                                onClick={() => setDeleteRow(row)}
                                className="px-2.5 py-1.5 rounded-lg bg-fieldiq-card border border-red-500/40 text-xs font-semibold text-white hover:border-red-500/70"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            </div>
          )}

          {groups.length > 0 && (
            <p className="text-xs text-fieldiq-text">
              Rate change starts a new rate from a date. Correct fixes an existing rate.
            </p>
          )}
        </SettingsCard>
      </SettingsPage>

      {modalMode && (
        <TaxRateRowModal
          mode={modalMode}
          initialValues={modalValues}
          country={form.country}
          existingRow={modalRow}
          standardRates={standardRates}
          standardRatesFailed={standardRatesFailed}
          saving={saveRow.isPending}
          onCancel={() => setModalMode(null)}
          onSubmit={payload => saveRow.mutate(payload)}
        />
      )}

      <ConfirmModal
        isOpen={deleteRow !== null}
        title="Delete this jurisdiction rate?"
        confirmLabel={removeRow.isPending ? 'Deleting…' : 'Delete'}
        loading={removeRow.isPending}
        onCancel={() => setDeleteRow(null)}
        onConfirm={() => deleteRow && removeRow.mutate(deleteRow.id)}
        body={
          deleteRow ? (
            /* Theme ink, not the modal's own grey: ConfirmModal's body class is a
               fixed light grey that reads 1.47:1 on its white light-mode panel
               (measured 2026-10-08). Set here rather than in the shared modal,
               which 48 screens use and which has not been swept. */
            <div className="space-y-2 text-fieldiq-text">
              <p>
                {deleteRow.jurisdiction}{regionName(deleteRow.jurisdiction) === deleteRow.jurisdiction ? '' : ` (${regionName(deleteRow.jurisdiction)})`},
                effective {day(deleteRow.effective_from)}.
              </p>
              {tenantToday && rowEffect(deleteRow, rows, tenantToday) === 'in_effect' ? (
                successor ? (
                  <p>
                    Issued invoices keep their rate. New documents in {deleteRow.jurisdiction} will use the rate from {day(successor.effective_from)} —{' '}
                    {taxSystemLabel(successor.tax_system)}, {fmtTaxRate(successor.tax_rate_1)} and {fmtTaxRate(successor.tax_rate_2)}.
                  </p>
                ) : (
                  <p className="font-semibold">
                    Issued invoices keep their rate. New documents in {deleteRow.jurisdiction} will use your older company settings.
                  </p>
                )
              ) : (
                <p>Issued invoices keep their rate.</p>
              )}
            </div>
          ) : ''
        }
      />
    </div>
  );
}
